import React, { useCallback, useMemo } from 'react';
import { Components } from 'formiojs';
import type { ExtendedComponentSchema } from 'formiojs';
import { EhsFormioReactComponent } from '../';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { TimePicker } from '../../../form';
import type { Component } from '@formio/react';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioTimePickerConstructor = Record<string, never>;
export type EhsFormioTimePickerSettings = Record<string, never>;
export const EhsFormioTimePickerType = 'ehstimepicker';

export class EhsFormioTimePicker extends EhsFormioReactComponent<
  EhsFormioTimePickerConstructor,
  EhsFormioTimePickerSettings
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioTimePickerType,
      label: 'common.time',
      key: EhsFormioTimePickerType,
      ...extend
    });
  }

  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-Timeinput',
      group: 'advanced',
      icon: 'calendar',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioTimePicker.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  extractFields(component: Component<EhsFormioTimePickerConstructor>): void {
    this.shouldSetValue = true;
    this.dataForSetting = this._data[component.key as string];
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioTimePickerSettings>) => JSX.Element,
    EhsFormioTimePickerSettings
  ] {
    return [TimeInputCmp, {}];
  }
}

//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link FormioTimeInputSettings} properties
 */
function TimeInputCmp({
  value,
  onChange,
  label,
  readOnly,
  required,
  defaultValue,
  keyForComponent
}: EhsFormioReactProps<EhsFormioTimePickerSettings>): JSX.Element {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );
  /**
   * Formio expect time in format 99:99 (HH:mm), but our TimePicker component expects time in Date format.
   * Therefore, we are converting the date to time string.
   */
  const onChangeWrapper = useCallback(
    (selectedDates: null | string | Date): void => {
      if (!selectedDates) {
        onChange('');
        return;
      }

      if (isDate(selectedDates)) {
        onChange(dateToTimeString(selectedDates));
        return;
      }

      if (isTimeString(selectedDates)) {
        onChange(selectedDates);
        return;
      }

      if (isDateString(selectedDates)) {
        onChange(dateToTimeString(new Date(selectedDates)));
        return;
      }

      console.error('Invalid time string: ', selectedDates);
      onChange('');
    },
    [onChange]
  );

  /**
   * TimePicker component expects time in Date format,
   * so we are converting the date to time string in format HH:mm.
   */
  const wrapperValue = useMemo(() => {
    if (value) {
      return timeStringToDate(value);
    }

    if (defaultValue) {
      return timeStringToDate(defaultValue);
    }

    return null;
  }, [defaultValue, value]);

  return useMemo(() => {
    return (
      <TimePicker
        onChange={onChangeWrapper}
        value={wrapperValue}
        label={label}
        readOnly={readOnly}
        required={required}
        error={errorMessage}
        labelOnTop={isQuestionnaire}
      />
    );
  }, [
    onChangeWrapper,
    wrapperValue,
    label,
    readOnly,
    required,
    errorMessage,
    isQuestionnaire
  ]);
}

/**
 * Convert a time string in the format "HH:mm" into a JavaScript Date object.
 *
 * @param timeString time string in the format "HH:mm"
 */
function timeStringToDate(timeString: string): Date | null {
  const [hours, minutes] = timeString.split(':').map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }

  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes
  );
}

/**
 * Convert a JavaScript Date object into a time string in the format "HH:mm".
 *
 * @param date JavaScript Date object
 */
function dateToTimeString(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

// created by AI - begin
function isDate(date: any): date is Date {
  return date instanceof Date && !isNaN(date.getTime());
}

function isTimeString(timeString: string) {
  const regex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return regex.test(timeString);
}

function isDateString(dateString: string) {
  return !isNaN(Date.parse(dateString));
}

// created by AI - end
